import { Box, Flex, Text } from '@chakra-ui/react';
import { memo, useEffect, useMemo } from 'react';
import { preventDefault } from 'utils/event';

import { AutoCompleteInputListProps } from './AutoCompleteInput.types';
import { useAutoCompleteContext } from './AutoCompleteInputContext';

export const AutoCompleteInputList = memo(
  ({ children, showNoResultsFound, useRelativePosition }: AutoCompleteInputListProps) => {
    const { value, selectedIds, isListVisible, activeIndex, listRef, addSelectedId, setItems, activeDescendentId } =
      useAutoCompleteContext();

    const parsedChildren = useMemo(() => (Array.isArray(children) ? children : [children]), [children]);
    const filteredChildren = useMemo(() => {
      return parsedChildren
        .filter((child) => !selectedIds.includes(child.props.value))
        .filter((child) => child.props.name.toString().toLowerCase().includes(value.toLowerCase()));
    }, [value, parsedChildren, selectedIds]);

    const showNotFoundMsg = useMemo(() => {
      return showNoResultsFound && !filteredChildren.length && value && !selectedIds.length;
    }, [showNoResultsFound, filteredChildren.length, value, selectedIds.length]);

    useEffect(() => {
      if (!children) return;

      // Update the items in the AutoCompleteContext when the parent component changes the children element
      setItems(
        parsedChildren.map((child) => ({
          name: child.props.name,
          value: child.props.value,
        })),
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children, parsedChildren.length]);

    return (
      <Flex position='relative' zIndex={'popover'} visibility={isListVisible ? 'visible' : 'hidden'}>
        <Flex
          as='ul'
          ref={listRef}
          width='full'
          // If useRelativePosition is true,
          // the position is relative when isListVisible is true; otherwise, it's absolute.
          position={useRelativePosition ? (isListVisible ? 'relative' : 'absolute') : 'absolute'}
          direction='column'
          background='white'
          borderBottomRadius={4}
          boxShadow='sm'
          cursor={showNotFoundMsg ? 'auto' : 'pointer'}
          aria-orientation='vertical'
          role='listbox'
        >
          {showNotFoundMsg ? (
            <Box p='2'>
              <Text fontSize='sm'>
                {`Nothing found for `}
                <em>{value}</em>
              </Text>
            </Box>
          ) : (
            filteredChildren.map((child, index) => {
              const isActive = index === activeIndex;
              const onClick = () => addSelectedId(child.props.value);

              return (
                <Flex
                  key={child.props.value}
                  as='li'
                  role='option'
                  aria-selected={isActive}
                  id={isActive ? activeDescendentId : undefined}
                  // Prevent blur event triggering on the input element - otherwise, will close the list before the option is clicked
                  onMouseDown={preventDefault}
                  onClick={onClick}
                  _selected={{ backgroundColor: 'brand.100' }}
                >
                  {child}
                </Flex>
              );
            })
          )}
        </Flex>
      </Flex>
    );
  },
);

AutoCompleteInputList.displayName = 'AutoCompleteInputList';
